import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileDrop from "@/components/admin/FileDrop";
import { Trash2, Pencil, Plus, ExternalLink, Sparkles, Pin, ArrowUp, ArrowDown } from "lucide-react";

const TYPES = ["news", "facebook", "youtube", "twitter", "other"];
const EMPTY = { title: "", url: "", source: "", coverage_type: "news", date: "", date_iso: "", description: "", image: "", pil_id: null, initiative_id: null };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }

export default function CoverageAdmin() {
  const [items, setItems] = useState([]);
  const [pils, setPils] = useState([]);
  const [inits, setInits] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [fetching, setFetching] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/coverage").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/pils?include_unpublished=true&limit=200").then(({ data }) => setPils(data.items)).catch(() => {});
    api.get("/initiatives?include_unpublished=true").then(({ data }) => setInits(data)).catch(() => {});
  }, []);

  const fetchPreview = async () => {
    if (!f.url) return toast.error("Paste a link first");
    setFetching(true);
    try {
      const { data } = await api.post("/coverage/preview", { url: f.url });
      setF((p) => ({ ...p, title: p.title || data.title || "", source: p.source || data.source || "",
        image: data.image || p.image || "", date: data.date || p.date || "",
        date_iso: data.date_iso || p.date_iso || "",
        coverage_type: data.coverage_type || p.coverage_type }));
      if (data.date_iso) toast.success("Details and news date fetched from link");
      else toast.warning("Details fetched — news date not found on this page, please set it manually");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fetch preview"); }
    finally { setFetching(false); }
  };

  const edit = (c) => { setF({ ...EMPTY, ...c }); setOpen(true); };
  const save = async () => {
    if (!f.title || !f.url) return toast.error("Title and link are required");
    const payload = { ...f };
    if (payload.date_iso) {
      const d = new Date(payload.date_iso + "T00:00:00");
      if (!isNaN(d)) payload.date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
    }
    try {
      if (f.id) await api.put(`/coverage/${f.id}`, payload);
      else await api.post("/coverage", payload);
      toast.success("Saved"); setOpen(false); setF(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => { if (!window.confirm("Remove this link?")) return; await api.delete(`/coverage/${c.id}`); toast.success("Removed"); load(); };
  const togglePin = async (c) => {
    try { await api.patch(`/coverage/${c.id}/pin`, { pinned: !c.pinned }); load(); }
    catch { toast.error("Could not update pin"); }
  };
  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/coverage/reorder", { ids: arr.map((c) => c.id) }); }
    catch { toast.error("Could not save order"); load(); }
  };
  const pilTitle = (id) => pils.find((p) => p.id === id)?.title;
  const initTitle = (id) => inits.find((i) => i.id === id)?.title;

  return (
    <>
      <AdminHeader title="Media Coverage" subtitle={`${items.length} external links`} actions={
        <Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-coverage-btn"><Plus size={15} className="mr-1.5" /> Add Coverage Link</Button>
      } />
      <AdminPage>
        <p className="text-sm text-slate-500 mb-4">Paste links to news articles, Facebook posts, YouTube videos or any online content covering the Trust's initiatives and PILs. Linked items appear under "In the Media" on the related PIL or initiative page.</p>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {items.length === 0 ? <p className="p-6 text-slate-400 text-sm">No coverage links yet.</p> : items.map((c, idx) => (
            <div key={c.id} className={`flex items-center justify-between px-4 py-3 ${c.pinned ? "bg-npst-goldLight/50" : ""}`} data-testid="coverage-admin-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {c.pinned && <span className="text-[10px] uppercase tracking-wider bg-npst-navy text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid="coverage-pinned-badge"><Pin size={9} /> Pinned</span>}
                  <span className="text-[10px] uppercase tracking-wider bg-npst-goldLight text-npst-gold border border-npst-goldBorder px-1.5 py-0.5 rounded">{c.source || c.coverage_type}</span>
                  <span className="font-medium text-npst-ink truncate">{c.title}</span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:text-npst-navy inline-flex items-center gap-1">{c.url.slice(0, 60)} <ExternalLink size={11} /></a>
                  {c.pil_id && <span className="ml-2">· PIL: {pilTitle(c.pil_id)?.slice(0, 30) || "—"}</span>}
                  {c.initiative_id && <span className="ml-2">· Initiative: {initTitle(c.initiative_id)?.slice(0, 30) || "—"}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-400 shrink-0">
                <button onClick={() => togglePin(c)} title={c.pinned ? "Unpin" : "Pin to top"} className={c.pinned ? "text-npst-gold" : "hover:text-npst-gold"} data-testid="coverage-pin-btn"><Pin size={15} fill={c.pinned ? "currentColor" : "none"} /></button>
                <button onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up" className="hover:text-npst-navy disabled:opacity-30 disabled:hover:text-slate-400" data-testid="coverage-move-up-btn"><ArrowUp size={15} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} title="Move down" className="hover:text-npst-navy disabled:opacity-30 disabled:hover:text-slate-400" data-testid="coverage-move-down-btn"><ArrowDown size={15} /></button>
                <button onClick={() => edit(c)} className="hover:text-npst-navy" data-testid="coverage-edit-btn"><Pencil size={15} /></button>
                <button onClick={() => del(c)} className="hover:text-red-600" data-testid="coverage-delete-btn"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Coverage Link</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title / Headline"><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="coverage-title" /></Field>
            <Field label="Link (URL)">
              <div className="flex gap-2">
                <Input value={f.url} onChange={(e) => set("url", e.target.value)} onBlur={() => { if (f.url && (!f.title || !f.date_iso)) fetchPreview(); }} placeholder="https://…" data-testid="coverage-url" />
                <Button type="button" variant="outline" onClick={fetchPreview} disabled={fetching} data-testid="coverage-fetch-btn"><Sparkles size={14} className="mr-1.5" />{fetching ? "…" : "Fetch"}</Button>
              </div>
            </Field>
            {f.image && <img src={f.image} alt="preview" className="w-full h-40 object-cover rounded-md border border-slate-200" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <Field label="Upload Image / Document (optional — or use the link's auto-fetched image)">
              <FileDrop value={f.image} onChange={(v) => set("image", v)} label="Drag & drop an image, or click to browse" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Source"><Input value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="e.g. Zee News" /></Field>
              <Field label="Type"><Select value={f.coverage_type} onValueChange={(v) => set("coverage_type", v)}><SelectTrigger data-testid="coverage-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <Field label="News Date (determines ordering — newest first)"><Input type="date" value={f.date_iso || ""} onChange={(e) => set("date_iso", e.target.value)} data-testid="coverage-date" /></Field>
            <Field label="Short Description"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Related PIL"><Select value={f.pil_id || "__n"} onValueChange={(v) => set("pil_id", v === "__n" ? null : v)}><SelectTrigger data-testid="coverage-pil"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="__n">None</SelectItem>{pils.map((p) => <SelectItem key={p.id} value={p.id}>{p.title.slice(0, 40)}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Related Initiative"><Select value={f.initiative_id || "__n"} onValueChange={(v) => set("initiative_id", v === "__n" ? null : v)}><SelectTrigger><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="__n">None</SelectItem>{inits.map((i) => <SelectItem key={i.id} value={i.id}>{i.title.slice(0, 40)}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="coverage-save-btn">Save Link</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
